/**
 * formatPrice — display a price in its own currency, honestly.
 *
 * Joy Basket stores per-product currency (INR by default). This helper
 * formats prices using the visitor's locale's number conventions while
 * keeping the product's *own* currency symbol — so a ₹2,500 product
 * shows as "₹2,500" and a $29.99 product shows as "$29.99", regardless
 * of where the visitor is sitting.
 *
 * NO currency conversion. NO exchange rates. NO IP geolocation.
 * That kind of theater would mislead users — they can't actually buy
 * a FirstCry item from London, so showing "£24" is a lie. Each product
 * displays in the currency its underlying retailer charges.
 */

const NO_DECIMALS = new Set(['INR', 'JPY', 'KRW', 'VND', 'IDR', 'CLP']);

const SYMBOLS = {
  INR: '₹',
  USD: '$',
  GBP: '£',
  EUR: '€',
  AED: 'AED ',
  SGD: 'S$',
  AUD: 'A$',
  CAD: 'C$',
  JPY: '¥',
};

/**
 * @param {number|string} value         the numeric price
 * @param {string}        currencyCode  3-letter ISO code (default INR)
 * @returns {string}                    formatted price, or '' if value is invalid
 */
export const formatPrice = (value, currencyCode = 'INR') => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return '';

  const code = (currencyCode || 'INR').toUpperCase();
  const maxFractionDigits = NO_DECIMALS.has(code) ? 0 : 2;

  // Try the Intl API first — handles locale-correct grouping for every currency.
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: maxFractionDigits,
      minimumFractionDigits: 0,
    }).format(num);
  } catch {
    // Fallback if the runtime doesn't know this currency code
    const symbol = SYMBOLS[code] || `${code} `;
    const rounded = maxFractionDigits === 0 ? Math.round(num) : Number(num.toFixed(2));
    return `${symbol}${rounded.toLocaleString()}`;
  }
};

export const currencySymbol = (currencyCode = 'INR') => {
  const code = (currencyCode || 'INR').toUpperCase();
  return SYMBOLS[code] || code;
};

export default formatPrice;
